import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { IndividualLoginRequest, RequestCodeRequest } from '@domains/login';

import { apiIndividualLogin, apiRequestCode } from '@api/methods';

import { sendRequestSmsEvent } from '@utils/analytics';
import { FORMAT, VALIDATION_MESSAGES, VALIDATORS } from '@utils/constants';
import { handleFormError } from '@utils/form';
import { CodeStep, Step } from '@utils/login-dialog';
import { zeroPad } from '@utils/number';
import { trackException } from '@utils/sentry';
import { SnackBar } from '@utils/snackbar';

import { useTimer } from '@hooks/timer';

import { Button } from '@components/common/Button/Button';
import { InvisibleCaptcha } from '@components/common/InvisibleCaptcha/InvisibleCaptcha';
import { useInvisibleCaptcha } from '@components/common/InvisibleCaptcha/use-invisible-captcha';
import { Spinner } from '@components/common/Spinner/Spinner';
import { MaskedPatternInput } from '@components/common/StyledInput';

import styles from './LoginDialogCodeForm.module.scss';

type FormValues = {
    code: string;
};

export type LoginDialogCodeFormProps = Readonly<{
    step: CodeStep;
    setStep: (step: Step) => void;
    onSuccess: () => void;
}>;

export const LoginDialogCodeForm: FunctionComponent<LoginDialogCodeFormProps> = (props) => {
    const { step, onSuccess } = props;

    const invisibleCaptcha = useInvisibleCaptcha();
    const { getCaptchaToken, isCaptchaLoading } = invisibleCaptcha;

    const [resendDelay, setResendDelay] = useState(step.resendDelay);
    const { stopTimer, startTimer, resetTimer, valueInSeconds } = useTimer();

    useEffect(() => {
        resetTimer();
        startTimer();
    }, [startTimer, resetTimer]);

    const timeToResend = Math.max(0, resendDelay - valueInSeconds);

    useEffect(() => {
        if (timeToResend === 0) {
            stopTimer(true);
        }
    }, [stopTimer, timeToResend]);

    const canResend = timeToResend <= 0;
    const foldSecondsToResend = zeroPad(Math.floor(timeToResend % 60), 2);
    const foldMinutesToResend = zeroPad(Math.floor(timeToResend / 60), 2);

    const [isResendingCode, setIsResendingCode] = useState<boolean>(false);

    const handleResend = (): void => {
        (async () => {
            let captchaToken: string | undefined;
            try {
                captchaToken = await getCaptchaToken();
            } catch {
                return;
            }

            const payload: RequestCodeRequest = {
                phone: step.phone,
                captcha_token: captchaToken,
            };

            try {
                setIsResendingCode(true);
                const response = await apiRequestCode(payload);
                sendRequestSmsEvent();
                setResendDelay(response.resend_delay);
                resetTimer();
                startTimer();
            } catch (error) {
                SnackBar.handleApiError(error);
            } finally {
                setIsResendingCode(false);
            }
        })().catch(trackException);
    };

    // -------------------------------------------

    const defaultValues: FormValues = useMemo(() => ({ code: '' }), []);

    const form = useForm<FormValues>({ defaultValues, shouldUnregister: true });

    const handleSubmit = useCallback(
        async (data: FormValues) => {
            const { code } = data;

            const payload: IndividualLoginRequest = { code, phone: step.phone };

            try {
                await apiIndividualLogin(payload);
                onSuccess();
            } catch (error) {
                SnackBar.handleApiError(error);
                handleFormError(form, error);
            }
        },
        [form, onSuccess, step.phone],
    );

    useEffect(() => {
        const subscription = form.watch(({ code }) => {
            if (VALIDATORS.codeValidator(code) === true) {
                form.handleSubmit(handleSubmit);
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [form, handleSubmit]);

    return (
        <>
            <div className={styles.phoneStep}>
                <form onSubmit={form.handleSubmit(handleSubmit)}>
                    <Controller
                        control={form.control}
                        name="code"
                        rules={{
                            required: VALIDATION_MESSAGES.required,
                            validate: VALIDATORS.codeValidator,
                        }}
                        render={({ field, fieldState }) => (
                            <MaskedPatternInput
                                hintMessage={fieldState.error?.message}
                                hasError={fieldState.error?.message !== undefined}
                                label="Код из смс"
                                format={FORMAT.code}
                                mask={' '.repeat(FORMAT.code.length)}
                                patternChar="#"
                                disabled={
                                    form.formState.isSubmitting ||
                                    isResendingCode ||
                                    isCaptchaLoading
                                }
                                isKeepHintPlace
                                {...field}
                            />
                        )}
                    />

                    <Button
                        type="submit"
                        variant="green"
                        isFullWith
                        isLoading={form.formState.isSubmitting}
                        disabled={isResendingCode || isCaptchaLoading}
                        data-testid="login-submit-code-btn"
                    >
                        Отправить
                    </Button>
                </form>
                <button
                    type="button"
                    disabled={
                        !canResend ||
                        form.formState.isSubmitting ||
                        isResendingCode ||
                        isCaptchaLoading
                    }
                    className={styles.resendTime}
                    onClick={handleResend}
                    data-testid="login-resend-code-btn"
                >
                    {(isCaptchaLoading || isResendingCode) && <Spinner size="medium" />}
                    {!(isCaptchaLoading || isResendingCode) &&
                        `Отправить повторно ${
                            !canResend ? `через ${foldMinutesToResend}:${foldSecondsToResend}` : ''
                        }`}
                </button>

                <InvisibleCaptcha {...invisibleCaptcha} />
            </div>
        </>
    );
};
